import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { MapIcon, RefreshCwIcon, FileTextIcon, BookOpenIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'roadmap', label: 'Yol Haritası', icon: MapIcon, file: 'ROADMAP.md' },
  { id: 'changelog', label: 'Değişiklikler', icon: FileTextIcon, file: 'CHANGELOG.md' },
  { id: 'prd', label: 'PRD', icon: BookOpenIcon, file: 'PRD.md' },
];

const UltraRoadmap = () => {
  const [active, setActive] = useState('roadmap');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const r = await axios.get(`${BACKEND_URL}/api/ultra-admin/roadmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data || {});
    } catch (e) {
      console.error('roadmap load error', e);
      toast.error('Yol haritası yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Stats — count P0/P1/P2/DONE in roadmap text
  const text = data[active] || '';
  const stats = active === 'roadmap' ? {
    p0: (text.match(/^\s*##\s+🚀\s+P0/gm) || []).length,
    p1: (text.match(/^\s*##\s+🟡\s+P1/gm) || []).length,
    p2: (text.match(/^\s*##\s+🟢\s+P2/gm) || []).length,
    todo: (text.match(/^\s*-\s+\[\s\]/gm) || []).length,
    done: (text.match(/^\s*-\s+\[x\]/gim) || []).length,
  } : null;

  return (
    <div className="p-6 space-y-6" data-testid="ultra-roadmap">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MapIcon className="w-8 h-8 text-purple-600" />
            Yol Haritası & Sürüm Geçmişi
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Aktif geliştirme planı · `/app/memory/` altındaki canlı dosyalar
          </p>
        </div>
        <Button onClick={fetchAll} disabled={loading} variant="outline" data-testid="roadmap-refresh-btn">
          <RefreshCwIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Stats (only for roadmap) */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
            <div className="text-xs text-red-700">🚀 P0 Bölüm</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{stats.p0}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
            <div className="text-xs text-amber-700">🟡 P1 Bölüm</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{stats.p1}</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
            <div className="text-xs text-emerald-700">🟢 P2 Bölüm</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.p2}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-gray-500">⬜ Açık Madde</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.todo}</div>
          </Card>
          <Card className="p-4 bg-purple-50/50 border-purple-200">
            <div className="text-xs text-purple-700">✅ Tamamlandı</div>
            <div className="text-2xl font-bold text-purple-700 mt-1">{stats.done}</div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`roadmap-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                isActive
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              <span className="text-xs text-gray-400">({t.file})</span>
            </button>
          );
        })}
      </div>

      {/* Markdown content */}
      <Card className="p-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
        ) : !text ? (
          <div className="text-center py-12 text-gray-400">
            <FileTextIcon className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p>{TABS.find(t => t.id === active)?.file} dosyası bulunamadı veya boş.</p>
          </div>
        ) : (
          <div
            className="prose prose-sm max-w-none
              prose-headings:text-gray-900
              prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-4
              prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
              prose-h3:text-lg prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-gray-800
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-li:text-gray-700 prose-li:my-0.5
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-purple-700 prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
              prose-hr:my-8 prose-hr:border-gray-200
              prose-a:text-purple-600 prose-a:no-underline hover:prose-a:underline
              prose-table:text-sm
              prose-th:bg-gray-50 prose-th:text-left prose-th:p-2
              prose-td:p-2 prose-td:border-t prose-td:border-gray-100"
            data-testid="roadmap-markdown"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                li: ({ node, ...props }) => {
                  // Custom rendering for checkbox tasks
                  const checked = node.children?.[0]?.value?.startsWith?.('[x]') ||
                                  node.children?.[0]?.checked === true;
                  return <li {...props} className={checked ? 'opacity-60' : ''} />;
                },
                input: ({ node, ...props }) => (
                  <input
                    {...props}
                    disabled
                    className={`mr-2 align-middle ${props.checked ? 'accent-emerald-500' : ''}`}
                  />
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          </div>
        )}
      </Card>

      <p className="text-xs text-gray-400 text-center">
        Bu sayfa salt-okunur. Düzenlemek için ekibinizden ilgili `.md` dosyasını düzenleyip deploy etmesini isteyin.
      </p>
    </div>
  );
};

export default UltraRoadmap;
